/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */
import { LazyPicture } from "@eisinc/MobiusOverrides/LazyImage";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from '@insite/client-framework/Store/Context/ContextSelectors';
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { WidgetDefinition } from "@insite/client-framework/Types/ContentItemDefinitions";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

export const mapStateToProps = (state: ApplicationState) => ({
    bynderBaseUrl: (getSettingsCollection(state).customSettings as any)?.bynderBaseUrl,
});

export interface ImageStyles {
    wrapper: InjectableCss;
    image: InjectableCss;
    pictureProps?: JSX.IntrinsicElements["picture"];
}

const enum fields {
    smallImagePath = "smallImagePath",
    mediumImagePath = "mediumImagePath",
    largeImagePath = "largeImagePath",
    altText = "altText",
    link = "link",
    openInNewTab = "openInNewTab",
    horizentalAlignment = "horizentalAlignment",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.smallImagePath]: string;
        [fields.mediumImagePath]: string;
        [fields.largeImagePath]: string;
        [fields.altText]: string;
        [fields.link]: LinkFieldValue;
        [fields.openInNewTab]: boolean;
        [fields.horizentalAlignment]: string;
        [fields.customCSS]: string;
    };
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export const imageStyles: ImageStyles = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: ${(props: any) =>
                props?.fields?.horizentalAlignment === "center"
                    ? "center"
                    : props?.fields?.horizentalAlignment === "left"
                        ? "flex-start"
                        : "flex-end"};
        `,
    },
    image: {
        css: css`
            width: auto;
            height: auto;
            img {
                width: auto;
                max-width: 100%;
                max-height: 100%;
            }
        `,
    },
    pictureProps: {
        style: {
            lineHeight: 0,
        },
    },
};

const styles = imageStyles;

const StyledWrapperX = getStyledWrapper("div") as any;

const Image: FC<Props> = ({ fields, bynderBaseUrl }) => {
    const pageUrl = useGetLink(fields.link)?.url || "";

    const [error, setError] = React.useState(false);
    if (error) {
        return null;
    }
    const ImagesComponent = (
        <LazyPicture
            smallImageSrc={fields.smallImagePath}
            mediumImageSrc={fields.mediumImagePath}
            largeImageSrc={fields.largeImagePath}
            altText={fields.altText}
            pictureProps={styles.pictureProps}
            onError={() => setError(true)}
            bynderBaseUrl={bynderBaseUrl}
        />
    );

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapperX {...styles.wrapper} fields={fields}>
            <StyledWrapper {...customCssWrapper}>
                {pageUrl ? (
                    <Link href={pageUrl} target={fields.openInNewTab ? "_blank" : "_self"}>
                        {ImagesComponent}
                    </Link>
                ) : (
                    <>{ImagesComponent}</>
                )}
            </StyledWrapper>
        </StyledWrapperX>
    );
};

const defaultCustomCss = `.img-wrapper {
}

.img-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(Image),
    definition: {
        group: "Basic",
        displayName: "Image",
        icon: "image",
        fieldDefinitions: [
            {
                name: fields.smallImagePath,
                displayName: "Small Image Path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.mediumImagePath,
                displayName: "Medium Image Path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.largeImagePath,
                displayName: "Large Image Path",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
                isRequired: true,
            },
            {
                name: fields.altText,
                displayName: "Alt Text",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Contextual",
                tab: basicTab,
                isRequired: false,
            },
            {
                name: fields.link,
                displayName: "Link",
                editorTemplate: "CustomLinkField" as any,
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                tab: basicTab,
                isRequired: false,
            },
            {
                name: fields.openInNewTab,
                displayName: "Open In New Tab",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                tab: basicTab,
                isRequired: false,
            },
            {
                name: fields.horizentalAlignment,
                displayName: "Horizontal Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "left" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "right" },
                ],
                hideEmptyOption: true,
                defaultValue: "center",
                fieldType: "General",
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "Contextual",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
